import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  Paper,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { login, userInfo } from "../../features/slices/Auth";
import { AppDispatch, RootState } from "../../store";
import { useLoader } from "../../context/LoaderContext";
import { useSnackbar } from "../../context/SnackbarContext";

const AUTH_PAGES = [
  "/signin",
  "/signup",
  "/reset-password",
  "/reset-password/request",
  "/",
];

function isAuthPage(path: string) {
  return AUTH_PAGES.some(
    (authPath) => path === authPath || path.startsWith(authPath + "?")
  );
}

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { showLoader, hideLoader } = useLoader();
  const { showSnackbar } = useSnackbar();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      showLoader();
      const response = await dispatch(login({ email, password }));
      if (login.fulfilled.match(response)) {
        dispatch(userInfo());
        const redirectTo = localStorage.getItem("redirectTo") || "/suites";
        if (!isAuthPage(redirectTo)) {
          navigate(redirectTo, { replace: true });
        } else {
          navigate("/suites", { replace: true });
        }
        hideLoader();
        showSnackbar("Login successful!", "success");
        localStorage.removeItem("redirectTo");
      } else {
        showSnackbar(response.payload, "error");
        hideLoader();
      }
    } catch (error: any) {
      let backendMsg =
        (error?.response?.data?.message || error?.message) ??
        "Login failed. Please check your credentials.";
      showSnackbar(backendMsg, "error");
      hideLoader();
      console.error("Failed to login:", error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = localStorage.getItem("redirectTo") || "/suites";
      if (!isAuthPage(redirectTo)) {
        navigate(redirectTo, { replace: true });
      } else {
        navigate("/suites", { replace: true });
      }
      localStorage.removeItem("redirectTo");
    }
  }, [isAuthenticated]);

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: 4,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <LockOutlinedIcon />
            </Avatar>
            <Box>
              <Typography component="h1" variant="h5" fontWeight={700}>
                Sign In
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Access DeviceSure orchestration
              </Typography>
            </Box>
          </Box>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              Sign In
            </Button>
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Forgot password?
                </Typography>
                {/* <Link
                  href="/reset-password/request"
                  variant="body2"
                  underline="hover"
                >
                  Forgot password?
                </Link> */}
              </Grid>
              <Grid item>
                <Link href="/signup" variant="body2" underline="hover">
                  Don't have an account? Sign Up
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
